import { useCallback, useMemo } from 'react';

import { useCustomSetting } from '@/_pages/system-users/shared/custom-settings/hooks';

import { COMPANY_FILTERS_CONFIG, type CompanyFilterSettingsSchema } from '../settings';

type UseCompanyFilterPersistenceProps = {
  defaultValue?: CompanyFilterSettingsSchema;
};

export function useCompanyFilterPersistence({
  defaultValue = COMPANY_FILTERS_CONFIG.defaultValue,
}: UseCompanyFilterPersistenceProps = {}) {
  const { value: currentFilters, updateValue, isUpdating } = useCustomSetting(COMPANY_FILTERS_CONFIG);

  // Update specific filter value
  const updateFilter = useCallback(
    async (filterKey: keyof CompanyFilterSettingsSchema, newValue: CompanyFilterSettingsSchema[typeof filterKey]) => {
      const updatedFilters = {
        ...currentFilters,
        [filterKey]: newValue,
      } as CompanyFilterSettingsSchema;

      try {
        await updateValue(updatedFilters);
      } catch (error) {
        console.error('Failed to update company filter:', error);
        throw error;
      }
    },
    [currentFilters, updateValue]
  );

  const revenueType = useMemo(
    () => currentFilters.revenueType || defaultValue.revenueType || 'gross',
    [currentFilters.revenueType, defaultValue.revenueType]
  );

  const consolidatedByYear = useMemo(
    () => currentFilters.consolidatedByYear ?? defaultValue.consolidatedByYear ?? false,
    [currentFilters.consolidatedByYear, defaultValue.consolidatedByYear]
  );

  return {
    currentFilters,
    revenueType,
    consolidatedByYear,
    updateFilter,
    isUpdating,
  };
}
